<template>
  <div>
    <b-breadcrumb>
      <b-breadcrumb-item to="/">
        <i class="fa fa-home"></i>
        Home
      </b-breadcrumb-item>
      <b-breadcrumb-item to="/FinancialServices/NetworkIDTs"
        >Financial Services</b-breadcrumb-item
      >
      <b-breadcrumb-item active>Network IDTs</b-breadcrumb-item>
    </b-breadcrumb>
    <div class="card">
      <div class="bg-dark card-header text-white">
        <div class="row">
          <div class="col text-start">Network IDTs</div>
          <div class="col" style="text-align:right;">Total: {{ totalAmountCharged | currency }}</div>
        </div>
      </div>
      <div class="card-body">
        <AppNotification type="info" v-if="currentNetworkIDTNumber">
          Current Network IDT Number is {{currentNetworkIDTNumber}}
        </AppNotification>

        <!-- The collapsable filter options -->
        <AppFormCardCollapsable
          :display-footer="true"
          class="mb-3"
          @submit.prevent="onFilterSubmit()"
        >
          <template v-slot:header>Filter</template>
          <template v-slot:body>
            <div class="form-row">
              <div class="col-md-4 col-sm-6 form-group">
                <label>SL</label>
                <input
                  class="form-control"
                  placeholder="SL Number"
                  v-model="filterOptions.sl"
                />
              </div>
              <div class="col-md-4 col-sm-6 form-group">
                <label>SA</label>
                <input
                  class="form-control"
                  placeholder="SA Number"
                  v-model="filterOptions.sa"
                />
              </div>
              <div class="col-md-4 col-sm-6 form-group">
                <label>FAMIS Department Code</label>
                <input
                  class="form-control"
                  placeholder="FAMIS Department Code"
                  v-model="filterOptions.famisDepartment"
                />
              </div>
            </div>
          </template>
          <template v-slot:footer>
            <button class="btn btn-primary" type="submit">Apply</button>
          </template>
        </AppFormCardCollapsable>

        <div class="d-print-none dropdown mb-3 text-right">
          <button
            aria-expanded="false"
            aria-haspopup="true"
            class="btn btn-secondary dropdown-toggle"
            data-toggle="dropdown"
            style="width: 100px"
            type="button"
            :disabled="isPerformingAction"
          >
            <span>Actions</span>
          </button>
          <div class="dropdown-menu">
            <RouterLink
              :to="{ name: 'AddNetworkIDT' }"
              class="btn btn-outline-light dropdown-item text-left"
              title="Add New Record"
            >
              <i class="fa fa-plus"></i>Add Record
            </RouterLink>
            <button class="btn btn-outline-light dropdown-item text-left" @click="isAddModalVisible=true">
              <i class="fa fa-plus"></i> Add Network IDT Number
            </button>
            <a @click.prevent="downloadNetworkIDTFrozenAndMissingAccounts()" class="btn btn-outline-light dropdown-item text-left"
              href="javascript:void(0)" title="Export missing and frozen network IDT account list to a CSV">
                <i class="fa fa-file-excel"></i>Download Missing and Frozen Accounts .csv
            </a>
          </div>
        </div>

        <BTable
          :busy="isLoading"
          :fields="tableFields"
          :items="networkIDTs"
          :responsive="true"
          show-empty
          striped
          table-class="table-align-middle"
        >
          <template v-slot:cell(null)="row">
            <div class="btn-group">
              <router-link
                :to="{
                  name: 'EditNetworkIDT',
                  params: {
                    sl: row.item.sl,
                    sa: row.item.sa,
                    famisDepartment: row.item.famisDepartment,
                    amountCharged: row.item.amountCharged
                 },
                }"
                class="btn btn-sm btn-warning d-print-none"
                data-toggle="tooltip"
                title="Edit Network IDT Details"
              >
                <i class="fa fa-pencil-alt"></i>Edit
              </router-link>
              <button @click="onDeleteClick(row.item)" class="btn btn-danger btn-sm d-print-none" type="button">
                <i class="fa fa-trash"></i>Delete
              </button>
            </div>
          </template>

          <template v-slot:cell(amountCharged)="row">
            {{ row.item.amountCharged | currency(true) }}
          </template>

          <template v-slot:cell(accountStatus)="row">
            {{ row.item.accountMissing ? 'Missing' : row.item.accountFrozen ? 'Frozen' : 'Active' }}
          </template>

          <!-- Footer Pagination -->
          <template v-slot:custom-foot>
            <b-tr>
              <b-td :colspan="tableFields.length">
                <AppTablePagination
                  :page.sync="filterOptions.page"
                  :pageSize.sync="filterOptions.pageSize"
                  :totalCount.sync="totalNetworkIDTs"
                  v-on:update:page="loadData()"
                  v-on:update:pageSize="onFilterSubmit()"
                ></AppTablePagination>
              </b-td>
            </b-tr>
          </template>
        </BTable>
        <TheNetworkIDTNumberAddForm 
          :visible="isAddModalVisible" 
          :currentNetworkIDTNumber="currentNetworkIDTNumber"
          @close="isAddModalVisible=false; loadData();" />
      </div>
      <AppModalConfirm :visible="deletingNetworkIDT" @close="deletingNetworkIDT=null" @confirm="onDeleteConfirmed(deletingNetworkIDT)">
        <template v-slot:header>Confirm</template>
        <p v-if="deletingNetworkIDT">Are you sure you want to delete the Network IDT for account {{deletingNetworkIDT.sl}}-{{deletingNetworkIDT.sa}} belonging to department {{deletingNetworkIDT.famisDepartment}}?</p>
      </AppModalConfirm>
    </div>
  </div>
</template>

<script>
import AppFormCardCollapsable from "@/components/shared/AppFormCardCollapsable";
import AppTablePagination from "@/components/shared/AppTablePagination";
import NetworkIDTService from "../../services/NetworkIDTsService";
import TheNetworkIDTNumberAddForm from "./TheNetworkIDTNumberAddForm.vue";
import AppNotification from "@/components/shared/AppNotification";
import { ToastType } from "@/services/models/shared/ToastType";
import {currency} from "@/filters/Currency";
import AppModalConfirm from "@/components/shared/AppModalConfirm";

export default {
  components: {
    AppFormCardCollapsable,
    AppTablePagination,
    TheNetworkIDTNumberAddForm,
    AppNotification,
    AppModalConfirm
  },
  filters: {
      currency
  },
  data() {
    return {
      networkIDTs: [],
      totalNetworkIDTs: 0,
      deletingNetworkIDT: null,
      filterOptions: {
        page: 1,
        pageSize: 10,
        sl: null,
        sa: null,
        famisDepartment: null
      },
      isLoading: false,
      tableFields: [
        { key: "null", label: "", thStyle: "width:175px" },
        { key: "sl", label: "SL" },
        { key: "sa", label: "SA" },
        { key: "famisDepartment", label: "FAMIS Department" },
        { key: "amountCharged", label: "Amount Charged" },
        { key: "accountStatus", label: "Account Status" }
      ],
      isAddModalVisible: false,
      isPerformingAction: false,
      totalAmountCharged: 0,
      currentNetworkIDTNumber: null
    };
  },
  methods: {
    loadData() {
      const vm = this;
      this.isLoading = true;

      NetworkIDTService.getNetworkIDTs(this.filterOptions)
        .then((responseOne) => {
          vm.networkIDTs = responseOne.data;
          vm.totalNetworkIDTs = responseOne.totalCount;

          NetworkIDTService.getTotalAmountCharged()
            .then((responseTwo) => {
              vm.totalAmountCharged = responseTwo;

              NetworkIDTService.getCurrentNetworkIDTNumber()
                .then((responseThree) => {
                  vm.currentNetworkIDTNumber = responseThree;
                })
                .catch(() => {
                  vm.redirectToError();
                })
                .finally(() => {
                  vm.isLoading = false;
                });
            })
            .catch(() => {
              vm.redirectToError();
            })
        })
        .catch(() => {
          vm.redirectToError();
        });
    },

    onFilterSubmit() {
      this.filterOptions.page = 1;
      this.loadData();
    },
    
    /**
     * @description Downloads the frozen and missing accounts list for network idts CSV export to the user's browser
     */
    downloadNetworkIDTFrozenAndMissingAccounts() {
        this.isPerformingAction = true;
        const vm = this;

        NetworkIDTService.downloadNetworkIDTFrozenAndMissingAccounts()
            .catch(() => {
                vm.alert(ToastType.Danger, "Unable to generate report. Please contact NIS if the issue persists.")
            })
            .finally(() => {
                this.isPerformingAction = false
            });
    },

    /**
     * @description Opens up a modal to confirm deletion of the network IDT
     */
    onDeleteClick(networkIDT) {
      this.deletingNetworkIDT = networkIDT;
    },
    
    /**
     * @description When the user clicks 'Confirm' - the selected network IDT will be deleted and the modal will be closed.
     */
    onDeleteConfirmed(deletingNetworkIDT) {
      const vm = this;
      vm.isPerformingAction = true;
    
      NetworkIDTService.deleteNetworkIDT(deletingNetworkIDT.sl, deletingNetworkIDT.sa, deletingNetworkIDT.famisDepartment)
        .then(() => {
          vm.alert(ToastType.Success, `The Network IDT was successfully deleted`);
          
          // Reload the data on the page
          vm.loadData();
        })
        .catch(() => {
          vm.alert(ToastType.Danger, "Failed to delete Network IDT. Please contact NIS if the issue persists.");
        })
        .finally(() => {
          vm.isPerformingAction = false;
          vm.deletingNetworkIDT = null;
        });
    },
  },
  created() {
    this.loadData();
  }
};
</script>
